import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarClock, Loader2, Save } from 'lucide-react';
import { api } from '@/api/client';

/** Saved under this settings key; the backend reads the same key when a B2B order is booked. */
const KEY = 'appointment_customers';

/** Same split the server uses: commas or new lines, blanks dropped, repeats ignored regardless of case. */
function parseNames(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of text.split(/[,\n\r]+/)) {
    const name = part.trim().replace(/\s+/g, ' ');
    const k = name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(name);
  }
  return out;
}

/**
 * Appointments Customer — the consignees whose B2B deliveries need a booked unloading slot.
 *
 * Any B2B order booked to one of these names gets an appointment raised automatically in the
 * Appointments module, so ops no longer have to key each LR in by hand.
 */
export function AppointmentCustomersPanel() {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [loaded, setLoaded] = useState('');
  // How the automatic carrier booking behaves. Saved in the same settings row as the list, because
  // it is the same rule: these customers, booked this way.
  const [autoBook, setAutoBook] = useState(true);
  const [defaultSlot, setDefaultSlot] = useState('10:00-18:00');
  const [poExpiryDays, setPoExpiryDays] = useState(7);
  const [dateOffsetDays, setDateOffsetDays] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['settings', KEY],
    queryFn: async () => (await api.get<any | null>(`/settings/${KEY}`)).data,
  });
  useEffect(() => {
    const v = data?.customers ?? '';
    setText(v);
    setLoaded(v);
    setAutoBook(data?.autoBook !== false);
    setDefaultSlot(String(data?.defaultSlot ?? '10:00-18:00'));
    setPoExpiryDays(Number(data?.poExpiryDays ?? 7));
    setDateOffsetDays(Number(data?.dateOffsetDays ?? 1));
  }, [data]);

  const names = useMemo(() => parseNames(text), [text]);
  const dirty = text !== loaded;

  const save = useMutation({
    // Stored tidy: one name per comma, in the order typed, so the box reads back cleanly.
    mutationFn: async () => (await api.put(`/settings/${KEY}`, {
      customers: names.join(', '),
      autoBook, defaultSlot: defaultSlot.trim(), poExpiryDays: Number(poExpiryDays) || 0, dateOffsetDays: Number(dateOffsetDays) || 0,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', KEY] });
      toast.success(`Saved ${names.length} customer${names.length === 1 ? '' : 's'}. New bookings use the list within a minute.`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save the list'),
  });

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><CalendarClock size={18} /> Appointments Customer</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          When a <strong>B2B</strong> order is booked to one of these customers, an appointment is raised for its LR automatically,
          whether it is 1 order or 100. It appears on the Appointments page as <strong>Pending</strong>, ready for the
          appointment date and PO.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Appointments Customer</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={12}
          placeholder="Blinkit, Zepto, Swiggy Instamart, BigBasket, Flipkart, Amazon"
          className="input w-full font-mono text-sm leading-relaxed resize-y"
        />
        <p className="text-xs text-slate-500 mt-1">
          Separate names with a comma or put each on its own line. Matching ignores capital letters and punctuation and looks for
          whole words, so <em>Blink</em> matches &ldquo;BLINK COMMERCE PVT LTD&rdquo; but not &ldquo;Blinkit&rdquo;.
          A very common word such as <em>Lifestyle</em> will also match unrelated consignees like &ldquo;Jindal Lifestyle&rdquo;,
          so use the fuller name where you can.
        </p>
      </div>

      {/* Automatic carrier booking. The carrier will not accept a slot without a date, a slot and a
          PO expiry, and none of the three exists when a consignment is booked — so these are what we
          ask for until the consignee gives the real ones. */}
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 space-y-3">
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={autoBook} onChange={(e) => setAutoBook(e.target.checked)} />
          <span className="text-sm text-indigo-900">
            <span className="font-medium">Ask the courier for the slot automatically</span>
            <span className="block text-indigo-700 text-xs mt-0.5">
              As soon as the LR comes back from the courier, the appointment is requested in the background. Only Delhivery B2B
              has an appointment API today; anything else is recorded as &ldquo;no appointment API&rdquo; and left for your team.
              A request that is refused is retried at most three times and the reason is shown on the appointment.
            </span>
          </span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Slot to ask for</label>
            {/* Delhivery LTL accepts these seven windows and refuses anything else. */}
            <select value={defaultSlot} onChange={(e) => setDefaultSlot(e.target.value)} className="input w-full">
              {['12:00 AM-07:00 AM','07:00 AM-10:00 AM','09:00 AM-12:00 PM','12:00 PM-03:00 PM','03:00 PM-06:00 PM','06:00 PM-09:00 PM','09:00 PM-11:59 PM']
                .map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Date when the order has no EDD</label>
            <select value={dateOffsetDays} onChange={(e) => setDateOffsetDays(Number(e.target.value))} className="input w-full">
              <option value={0}>Today</option>
              <option value={1}>Tomorrow</option>
              <option value={2}>In 2 days</option>
              <option value={3}>In 3 days</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">PO expiry (days after the slot)</label>
            <input type="number" min={0} max={365} value={poExpiryDays} onChange={(e) => setPoExpiryDays(Number(e.target.value))} className="input w-full" />
          </div>
        </div>
        <p className="text-[11px] text-indigo-800/80">
          <strong>The courier will not book without a PO number</strong>, and that comes from the consignee — so a consignment with
          no PO yet sits as &ldquo;Waiting for the PO number&rdquo; on the Appointments page and is booked automatically within ten
          minutes of somebody entering it. Nothing is lost while you wait.{' '}
        </p>
        <p className="text-[11px] text-indigo-800/80">
          The appointment date is the order&rsquo;s own promised delivery date (EDD) when it has one. Anything your team has
          already typed on the appointment — date, slot, PO — is used instead of these defaults, and whatever is finally sent is
          written back onto the appointment so the screen shows what the courier was actually asked for.
        </p>
      </div>

      {names.length > 0 && (
        <div>
          <div className="text-xs uppercase text-slate-500 mb-1.5">{names.length} customer{names.length === 1 ? '' : 's'} will be matched</div>
          <div className="flex flex-wrap gap-1.5">
            {names.map((n) => (
              <span key={n} className="px-2 py-0.5 rounded-full text-xs bg-slate-100 border border-slate-200 text-slate-700">{n}</span>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
        >
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>
    </div>
  );
}
